import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { Trash2, Edit, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';

export default function AdminPlans() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_usd: 0,
    otp_limit_monthly: 0,
    api_keys_limit: 0,
    max_phone_numbers: 1,
    support_level: 'email',
    slug: '',
  });

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/plans');
      setPlans(response.data.data || []);
    } catch (error) {
      setErrorMessage(t('Failed to fetch plans'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeletePlan = async (planId) => {
    if (!window.confirm(t('Are you sure you want to delete this plan?'))) return;
    try {
      await api.delete(`/admin/plans/${planId}`);
      setSuccessMessage(t('Plan deleted successfully'));
      fetchPlans();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || t('Failed to delete plan'));
    }
  };

  const handleEditPlan = (p) => {
    setEditingPlan(p);
    setFormData({
      name: p.name,
      description: p.description,
      price_usd: p.price_usd,
      otp_limit_monthly: p.otp_limit_monthly,
      api_keys_limit: p.api_keys_limit,
      max_phone_numbers: p.max_phone_numbers || 1,
      support_level: p.support_level || 'email',
      slug: p.slug || '',
    });
    setShowModal(true);
  };

  const handleSavePlan = async () => {
    if (!formData.name.trim()) {
      setErrorMessage(t('Plan name is required'));
      return;
    }

    try {
      const planData = {
        ...formData,
        slug: formData.slug.trim() || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      };

      if (editingPlan) {
        await api.put(`/admin/plans/${editingPlan.id}`, planData);
        setSuccessMessage(t('Plan updated successfully'));
      } else {
        await api.post('/admin/plans', planData);
        setSuccessMessage(t('Plan created successfully'));
      }

      setShowModal(false);
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        price_usd: 0,
        otp_limit_monthly: 0,
        api_keys_limit: 0,
        max_phone_numbers: 1,
        support_level: 'email',
        slug: '',
      });
      fetchPlans();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || t('Failed to save plan'));
    }
  };

  const resetForm = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      description: '',
      price_usd: 0,
      otp_limit_monthly: 0,
      api_keys_limit: 0,
      max_phone_numbers: 1,
      support_level: 'email',
      slug: '',
    });
  };

  return (
    <AdminLayout>
      <div dir={isRTL ? 'rtl' : 'ltr'} className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{t('Plans Management')}</h1>
            <p className="text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">{t('Create and manage subscription plans')}</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-[#128C7E] hover:bg-[#0F6B63] text-white rounded-lg sm:rounded-lg font-medium flex items-center gap-2 transition-colors whitespace-nowrap text-sm sm:text-base"
          >
            <Plus size={18} className="sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">{t('Add Plan')}</span><span className="sm:hidden">Add</span>
          </button>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-emerald-900/30 border border-emerald-700/50 rounded-lg flex items-center gap-3 text-sm sm:text-base">
            <CheckCircle size={18} className="sm:w-5 sm:h-5 text-emerald-400 flex-shrink-0" />
            <p className="text-emerald-300">{successMessage}</p>
          </div>
        )}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-700/50 rounded-lg flex items-center gap-3">
            <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
            <p className="text-red-300">{errorMessage}</p>
          </div>
        )}

        {/* Plans Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-400">{t('Loading plans...')}</div>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-12 bg-[#1A1F2E] border border-gray-800 rounded-lg">
            <p className="text-gray-400 mb-4">{t('No plans found')}</p>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="px-4 py-2 bg-[#128C7E] hover:bg-[#0F6B63] text-white rounded-lg font-medium inline-flex items-center gap-2 transition-colors"
            >
              <Plus size={18} />
              {t('Create first plan')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {plans.map((plan) => (
              <div key={plan.id} className="bg-[#1A1F2E] border border-gray-800 rounded-lg overflow-hidden hover:border-gray-700 transition-colors">
                <div className="bg-gradient-to-r from-[#128C7E]/10 to-transparent p-6 border-b border-gray-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                      {plan.description && (
                        <p className="text-sm text-gray-400 mt-1">{plan.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-[#128C7E]">${plan.price_usd || 0}</div>
                      <div className="text-xs text-gray-400">{t('per month')}</div>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {/* Plan Features */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">{t('OTP Limit')}:</span>
                      <span className="font-semibold text-white">{plan.otp_limit_monthly.toLocaleString()}/{t('month')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">{t('API Keys')}:</span>
                      <span className="font-semibold text-white">{plan.api_keys_limit}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">{t('Phone Numbers')}:</span>
                      <span className="font-semibold text-white">{plan.max_phone_numbers}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">{t('Support Level')}:</span>
                      <span className="px-3 py-1 bg-[#128C7E]/20 text-[#128C7E] rounded-full text-sm font-medium capitalize">
                        {t(plan.support_level)}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => handleEditPlan(plan)}
                      className="flex-1 px-3 py-2 bg-[#128C7E]/20 hover:bg-[#128C7E]/30 text-[#128C7E] rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Edit size={18} />
                      {t('Edit')}
                    </button>
                    <button
                      onClick={() => handleDeletePlan(plan.id)}
                      className="flex-1 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Trash2 size={18} />
                      {t('Delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-[#1A1F2E] border border-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#128C7E]/10 to-transparent p-6 border-b border-gray-800 sticky top-0 z-10">
              <h2 className="text-2xl font-bold text-white">
                {editingPlan ? t('Edit Plan') : t('Add New Plan')}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Plan Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('Plan Name')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('e.g. Starter, Professional, Enterprise')}
                  className="w-full px-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('Description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('Describe this plan briefly')}
                  rows="3"
                  className="w-full px-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/20 transition-all resize-none"
                />
              </div>

              {/* Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('Price (USD)')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      value={formData.price_usd}
                      onChange={(e) => setFormData({ ...formData, price_usd: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      className="w-full pl-8 pr-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* OTP Limit */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('OTP Limit/Month')}
                  </label>
                  <input
                    type="number"
                    value={formData.otp_limit_monthly}
                    onChange={(e) => setFormData({ ...formData, otp_limit_monthly: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    className="w-full px-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                    min="0"
                  />
                </div>
              </div>

              {/* API Keys & Phone Numbers */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('API Keys Limit')}
                  </label>
                  <input
                    type="number"
                    value={formData.api_keys_limit}
                    onChange={(e) => setFormData({ ...formData, api_keys_limit: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    className="w-full px-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('Max Phone Numbers')}
                  </label>
                  <input
                    type="number"
                    value={formData.max_phone_numbers}
                    onChange={(e) => setFormData({ ...formData, max_phone_numbers: parseInt(e.target.value) || 1 })}
                    placeholder="1"
                    className="w-full px-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                    min="1"
                  />
                </div>
              </div>

              {/* Support Level */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('Support Level')}
                </label>
                <select
                  value={formData.support_level}
                  onChange={(e) => setFormData({ ...formData, support_level: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                >
                  <option value="email">{t('Email')}</option>
                  <option value="priority">{t('Priority')}</option>
                  <option value="premium">{t('Premium')}</option>
                </select>
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t('Slug (auto-generated)')}
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder={t('Leave empty to auto-generate from name')}
                  className="w-full px-4 py-3 bg-[#0B0E14] border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/20 transition-all"
                />
                <p className="text-xs text-gray-500 mt-2">{t('Used in URLs and identifiers')}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-6 border-t border-gray-800">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={handleSavePlan}
                  className="px-6 py-3 bg-[#128C7E] hover:bg-[#0F6B63] text-white rounded-lg font-medium transition-colors"
                >
                  {editingPlan ? t('Update Plan') : t('Create Plan')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
